import {fromSeconds} from 'common/date';
import {DateTime} from 'luxon';

export const formatCents = (cents: number): string => {
  const dollars = Math.abs(cents) / 100;
  return `$${dollars.toFixed(2)}`;
};

/**
 * Like formatCents but keeps the sign, for amounts that can legitimately go
 * negative — e.g. a benefit whose linked clawbacks exceed its credits reads as
 * "-$10.00" rather than a misleading "$10.00".
 */
export const formatCentsSigned = (cents: number): string =>
  cents < 0 ? `-${formatCents(cents)}` : formatCents(cents);

/** Like formatCents but drops the cents when whole dollars: "$300", "$7.50" */
export const formatCentsShort = (cents: number): string => {
  const abs = Math.abs(cents);
  return abs % 100 === 0 ? `$${abs / 100}` : `$${(abs / 100).toFixed(2)}`;
};

/** Like formatCentsShort but keeps the sign for values that can go negative. */
export const formatCentsShortSigned = (cents: number): string =>
  cents < 0 ? `-${formatCentsShort(cents)}` : formatCentsShort(cents);

/**
 * Net values always carry an explicit sign ("+$613", "-$510") — on the
 * scorecard a bare number would be ambiguous about which side of the fee it
 * landed on.
 */
export const formatNetShort = (cents: number): string =>
  `${cents < 0 ? '-' : '+'}${formatCentsShort(cents)}`;

/**
 * The inclusive last covered day of a half-open period — what humans (and
 * card statements) call the end date. All rendering goes through this.
 */
export const inclusiveEndDay = (endSeconds: number): DateTime =>
  fromSeconds(endSeconds).minus({days: 1});

// Omits the start date's year when both dates share it ("Jan 1 – Jun 30,
// 2025"), so consecutive periods differ by their visible dates instead of
// repeating the year four times across two rows
export const formatDateRange = (
  startSeconds: number,
  endSeconds: number,
): string => {
  const start = fromSeconds(startSeconds);
  const end = inclusiveEndDay(endSeconds);
  const startStr = start.toLocaleString(
    start.year === end.year
      ? {month: 'short', day: 'numeric'}
      : {month: 'short', day: 'numeric', year: 'numeric'},
  );
  const endStr = end.toLocaleString({
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  return `${startStr} – ${endStr}`;
};

/**
 * Compact label for calendar-aligned periods: "July 2026" for an exact
 * month, "Q3 2026" for a quarter, "H1 2026" for a half, "2026" for a year,
 * "Jul – Sep 2026" for other whole-month spans. Anything not covering whole
 * months falls back to formatDateRange.
 */
export const formatPeriodLabel = (
  startSeconds: number,
  endSeconds: number,
): string => {
  const start = fromSeconds(startSeconds);
  // Half-open bounds: whole-month spans start and end on a month boundary
  const end = fromSeconds(endSeconds);

  const wholeMonths = start.day === 1 && end.day === 1;
  if (!wholeMonths) return formatDateRange(startSeconds, endSeconds);

  const months = (end.year - start.year) * 12 + (end.month - start.month);
  if (months === 1) {
    return start.toLocaleString({month: 'long', year: 'numeric'});
  }
  if (months === 3 && start.month % 3 === 1) {
    return `Q${Math.ceil(start.month / 3)} ${start.year}`;
  }
  if (months === 6 && (start.month === 1 || start.month === 7)) {
    return `H${start.month === 1 ? 1 : 2} ${start.year}`;
  }
  if (months === 12 && start.month === 1) {
    return String(start.year);
  }
  const lastMonth = inclusiveEndDay(endSeconds);
  if (start.year === lastMonth.year) {
    return `${start.toLocaleString({month: 'short'})} – ${lastMonth.toLocaleString({month: 'short'})} ${start.year}`;
  }
  return formatDateRange(startSeconds, endSeconds);
};

// Period bounds are half-open [start, end): the end timestamp is the first
// instant NOT covered, so expiry and containment are strict comparisons
export const isCurrentPeriod = (
  startSeconds: number,
  endSeconds: number,
): boolean => {
  const now = DateTime.now();
  return fromSeconds(startSeconds) <= now && now < fromSeconds(endSeconds);
};

/**
 * The single period to highlight among dated ranges (benefit periods or fee
 * periods): the active one if it contains today, otherwise the next upcoming
 * one, otherwise the most recent past one.
 */
export const findDisplayPeriod = <
  P extends {startDate: number; endDate: number},
>(
  periods: readonly P[],
): P | undefined => {
  const now = DateTime.now();
  let nextUpcoming: P | undefined;
  let mostRecentPast: P | undefined;

  for (const period of periods) {
    const pStart = fromSeconds(period.startDate);
    if (pStart <= now && now < fromSeconds(period.endDate)) {
      return period;
    }
    if (pStart > now) {
      if (!nextUpcoming || period.startDate < nextUpcoming.startDate) {
        nextUpcoming = period;
      }
    } else {
      if (!mostRecentPast || period.startDate > mostRecentPast.startDate) {
        mostRecentPast = period;
      }
    }
  }

  return nextUpcoming ?? mostRecentPast;
};
